import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Chip, 
  Typography, 
  Button,
  Stack
} from '@mui/material';
import { MedicalServices as MedicalServicesIcon, Add as AddIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../app/store';
import { addProcedureCode, removeProcedureCode } from '../../../../../services/CaseManagementService/policySlice';
import { RootState } from '../../../../../app/store';

const PolicyProcedureCodesEditor: React.FC = () => {
  const dispatch = useAppDispatch();
  const policy = useAppSelector((state: RootState) => state.policy.currentPolicy);
  const [inputValue, setInputValue] = useState('');
  
  const currentProcedureCodes = policy?.effectiveProcedureCodes || [];

  const handleAddCode = () => {
    if (inputValue.trim() && !currentProcedureCodes.includes(inputValue.trim())) {
      dispatch(addProcedureCode(inputValue.trim()));
      setInputValue('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddCode();
    }
  };

  const handleRemoveCode = (codeToRemove: string) => {
    dispatch(removeProcedureCode(codeToRemove));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <MedicalServicesIcon sx={{ color: '#64748b', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Effective Procedure Codes
        </Typography>
      </Stack>
      
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter CPT code (e.g., 29881)"
          variant="outlined"
          sx={{
            width: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#3b82f6',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
              }
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleAddCode}
          disabled={!inputValue.trim() || currentProcedureCodes.includes(inputValue.trim())}
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: '#3b82f6',
            '&:hover': {
              backgroundColor: '#2563eb'
            },
            '&:disabled': {
              backgroundColor: '#64748b'
            }
          }}
        >
          Add
        </Button>
      </Stack>

      {currentProcedureCodes.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {currentProcedureCodes.map((code) => (
            <Chip
              key={code}
              label={code}
              onDelete={() => handleRemoveCode(code)}
              size="small"
              sx={{
                backgroundColor: '#3b82f6',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: '#f1f5f9'
                  }
                }
              }}
            />
          ))}
        </Box>
      )}
      
      {currentProcedureCodes.length > 0 && (
        <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
          {currentProcedureCodes.length} procedure code{currentProcedureCodes.length !== 1 ? 's' : ''} added
        </Typography>
      )}
    </Box>
  );
};

export default PolicyProcedureCodesEditor;
